import { useCallback, useState, useId, forwardRef } from 'react';
import { Input, Button, Message, Checkbox } from '../';

import type { LoginFormProps, LoginCredentials } from './LoginForm.types';
import styles from './LoginForm.module.css';

/**
 * LoginForm component for authentication
 * Supports credential-based login with optional browser/SSO login toggle
 */
export const LoginForm = forwardRef<HTMLFormElement, LoginFormProps>(
  (
    {
      loginMethod = 'credentials',
      showSsoToggle = true,
      onSubmit,
      onSsoLogin,
      error,
      loading = false,
      logoSrc,
      logoAlt = 'Logo',
      urlLabel = 'URL (without https://)',
      description = 'Login to your IntelligenceBank account by entering your credentials below.',
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    const formId = useId();
    
    // Form state
    const [url, setUrl] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isCustomUrl, setIsCustomUrl] = useState(false);
    const [isBrowserLogin, setIsBrowserLogin] = useState(loginMethod === 'sso');

    // Show credentials fields when not in browser login mode
    const showCredentials = !isBrowserLogin;
    
    // Show browser login checkbox when loginMethod allows it and showSsoToggle is true
    const showBrowserLoginOption = showSsoToggle && (loginMethod === 'both' || loginMethod === 'sso');

    // Compute full URL
    const getFullUrl = useCallback((urlValue: string): string => {
      const trimmed = urlValue.trim().replace(/^https?:\/\//, '');
      if (!isCustomUrl && trimmed) {
        return trimmed + '.intelligencebank.com';
      }
      return trimmed;
    }, [isCustomUrl]);

    // Validation
    const isValid = showCredentials
      ? url.trim() !== '' && email.trim() !== '' && password.trim() !== ''
      : url.trim() !== '';

    // Handle form submission
    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!isValid) return;

        const fullUrl = getFullUrl(url);

        if (isBrowserLogin && onSsoLogin) {
          onSsoLogin(fullUrl);
        } else {
          const credentials: LoginCredentials = {
            url: fullUrl,
            email: email.trim(),
            password,
          };
          onSubmit(credentials);
        }
      },
      [isValid, url, email, password, isBrowserLogin, onSsoLogin, onSubmit, getFullUrl]
    );

    // Handle URL input change
    const handleUrlChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/^https?:\/\//, '');
        setUrl(value);
      },
      []
    );

    // Handle custom URL toggle
    const handleCustomUrlToggle = useCallback(
      (_event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>, checked: boolean) => {
        setIsCustomUrl(checked);
      },
      []
    );

    // Handle browser login toggle
    const handleBrowserLoginToggle = useCallback(
      (_event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>, checked: boolean) => {
        setIsBrowserLogin(checked);
      },
      []
    );

    const formClassNames = [styles.loginForm, className].filter(Boolean).join(' ');

    return (
      <div
        className={styles.loginWrapper}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        {logoSrc && (
          <div className={styles.logo} data-testid={testId && `${testId}-logo`}>
            <img src={logoSrc} alt={logoAlt} />
          </div>
        )}

        <div className={formClassNames}>
          {/* Description */}
          <p className={styles.description}>
            {description}
          </p>

          {/* Error message */}
          {error && (
            <Message
              variant="error"
              mode="banner"
              data-testid={testId && `${testId}-error`}
            >
              {error}
            </Message>
          )}

          {/* Form */}
          <form
            ref={ref}
            id={formId}
            className={styles.form}
            onSubmit={handleSubmit}
            noValidate
            data-testid={testId && `${testId}-form`}
          >
            {/* URL field */}
            <div className={styles.inputGroup}>
              {/* Label rendered separately to ensure full width regardless of domain suffix */}
              <label className={styles.urlLabel} htmlFor={`${formId}-url`}>
                {urlLabel}
                <span className={styles.required}>*</span>
              </label>
              <div className={styles.urlInputWrapper}>
                <Input
                  type="text"
                  id={`${formId}-url`}
                  placeholder={isCustomUrl ? 'mycompany.example.com' : 'mycompany'}
                  value={url}
                  onChange={handleUrlChange}
                  disabled={loading}
                  fullWidth
                  required
                  data-testid={testId && `${testId}-url`}
                />
                {!isCustomUrl && (
                  <span className={styles.domainSuffix} data-testid={testId && `${testId}-domain-suffix`}>
                    .intelligencebank.com
                  </span>
                )}
              </div>
              <div className={styles.checkboxRow}>
                <Checkbox
                  id={`${formId}-custom-url`}
                  label="Custom URL"
                  checked={isCustomUrl}
                  onChange={handleCustomUrlToggle}
                  disabled={loading}
                  data-testid={testId && `${testId}-custom-url-toggle`}
                />
              </div>
            </div>

            {/* Credentials fields - only shown when not in browser login mode */}
            {showCredentials && (
              <>
                <div className={styles.inputGroup}>
                  <Input
                    type="email"
                    label="Email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    fullWidth
                    required
                    autoComplete="email"
                    data-testid={testId && `${testId}-email`}
                  />
                </div>

                <div className={styles.inputGroup}>
                  <Input
                    type="password"
                    label="Password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    fullWidth
                    required
                    autoComplete="current-password"
                    data-testid={testId && `${testId}-password`}
                  />
                </div>
              </>
            )}

            {/* Submit button */}
            <div className={styles.buttons}>
              <Button
                type="submit"
                variant="primary"
                disabled={loading || !isValid}
                loading={loading}
                fullWidth
                data-testid={testId && `${testId}-submit`}
              >
                Login
              </Button>
            </div>

            {/* Browser Login checkbox */}
            {showBrowserLoginOption && (
              <div className={styles.browserLoginRow}>
                <Checkbox
                  id={`${formId}-browser-login`}
                  label="Browser Login (for SSO Users)"
                  checked={isBrowserLogin}
                  onChange={handleBrowserLoginToggle}
                  disabled={loading}
                  data-testid={testId && `${testId}-browser-login-toggle`}
                />
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }
);

LoginForm.displayName = 'LoginForm';

export default LoginForm;
